/**
 * Shared Geometry Functions for Shower Wall Tiling
 *
 * Pure geometry calculations with no SDK dependencies.
 * Used by both web-app and cloud functions.
 *
 * Module format: CommonJS (compatible with both environments)
 */

// The one source for 84". `bathroom-analyzer/pipeline/types` declares it and
// `designAutomation/rules/showerRules` already imports it rather than
// restating it — a combo's tile must reach the same height the analyzer
// reserves for the enclosure, or the two disagree about the same wall.
const {
  SHOWER_ENCLOSURE_HEIGHT_M,
} = require('../bathroom-analyzer/pipeline/types');
const { alcoveRecess } = require('../tub-surround/inferTubType');
const { wallFaceSpanY } = require('./wallFace');

const { isValidSystemChoice } = require('../shower-systems');

const INCH_M = 0.0254;

/**
 * The shower tile rule (decided 2026-10-21). A shower's wall tile is its
 * footprint on the wall, extended this far past each OPEN end — the glass
 * side, where tile that stopped flush with the curb would leave the wall
 * behind the door jamb bare...
 */
const SHOWER_TILE_MARGIN_IN = 2;
/**
 * ...and run on to the wall's end when it would otherwise stop within this
 * much of it, rather than leave a sliver of paint in the corner. A CLOSED end
 * (the shower built into the corner) is the same case at zero: its edge is
 * already at the wall's end.
 */
const SHOWER_TILE_SNAP_IN = 4;
/** "Standard (7′)": tile 84" up from the floor. The default. */
const SHOWER_TILE_STANDARD_HEIGHT_IN = 84;

/**
 * How high a shower's wall tile runs: Standard (84" from the floor) or Full
 * height (to the ceiling). Stored on each shower tile region as `tileHeight`,
 * so a move or a resize re-tiles at the height the user chose.
 */
const SHOWER_TILE_HEIGHTS = Object.freeze({
  STANDARD: 'standard',
  FULL: 'full',
});
const DEFAULT_SHOWER_TILE_HEIGHT = SHOWER_TILE_HEIGHTS.STANDARD;

const isShowerTileHeight = (value) =>
  value === SHOWER_TILE_HEIGHTS.STANDARD || value === SHOWER_TILE_HEIGHTS.FULL;

/**
 * The options every shower-tile caller hands `calculateShowerWallRegion` —
 * the cloud tiler, the cloud move/resize re-tile and the browser's — so they
 * cannot drift apart. A missing or unknown height is the default, Standard.
 *
 * A `shower` on a prefab system takes no margin (the user, 2026-10-29): its
 * panels are cut to its base and end flush with the curb's outer face, and
 * its glass stands mid-curb, so the jamb is on the panel without the 2".
 */
const showerTileRegionOptions = (tileHeight, shower = null) => ({
  marginIn: isValidSystemChoice(shower?.showerSystem)
    ? 0
    : SHOWER_TILE_MARGIN_IN,
  snapIn: SHOWER_TILE_SNAP_IN,
  height: isShowerTileHeight(tileHeight)
    ? tileHeight
    : DEFAULT_SHOWER_TILE_HEIGHT,
});

/**
 * Calculate distance between two points in XZ plane
 * @param {Object} p1 - First point {x, z}
 * @param {Object} p2 - Second point {x, z}
 * @returns {number} Distance between points
 */
function distance2D(p1, p2) {
  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Normalize a 2D vector
 * @param {Object} v - Vector {x, z}
 * @returns {Object} Normalized vector {x, z}
 */
function normalize2D(v) {
  const mag = Math.sqrt(v.x * v.x + v.z * v.z);
  if (mag === 0) return { x: 0, z: 0 };
  return { x: v.x / mag, z: v.z / mag };
}

/**
 * Calculate perpendicular distance from point to line segment
 * @param {Object} point - Point {x, z}
 * @param {Object} lineStart - Line segment start {x, z}
 * @param {Object} lineEnd - Line segment end {x, z}
 * @returns {number} Distance from point to line segment
 */
function pointToLineSegmentDistance(point, lineStart, lineEnd) {
  const dx = lineEnd.x - lineStart.x;
  const dz = lineEnd.z - lineStart.z;
  const lengthSquared = dx * dx + dz * dz;

  if (lengthSquared === 0) {
    return distance2D(point, lineStart);
  }

  // Parameter t determines where on the line segment the closest point is
  let t =
    ((point.x - lineStart.x) * dx + (point.z - lineStart.z) * dz) /
    lengthSquared;
  t = Math.max(0, Math.min(1, t));

  const closestPoint = {
    x: lineStart.x + t * dx,
    z: lineStart.z + t * dz,
  };

  return distance2D(point, closestPoint);
}

/**
 * Check if two line segments overlap (are collinear and share some portion)
 * @param {Object} edge - Edge {start: {x,z}, end: {x,z}}
 * @param {Object} wall - Wall segment {start: {x,z}, end: {x,z}}
 * @param {number} tolerance - Distance tolerance in meters
 * @returns {boolean} True if segments overlap
 */
function segmentsOverlap(edge, wall, tolerance = 0.08) {
  // Check if edge midpoint is close to wall LINE (not segment,
  // since the shower edge may extend beyond wall endpoints)
  const edgeMidpoint = {
    x: (edge.start.x + edge.end.x) / 2,
    z: (edge.start.z + edge.end.z) / 2,
  };

  const wDx = wall.end.x - wall.start.x;
  const wDz = wall.end.z - wall.start.z;
  const wLen = Math.sqrt(wDx * wDx + wDz * wDz);
  if (wLen === 0) return false;
  const nX = -wDz / wLen;
  const nZ = wDx / wLen;
  const distToWall = Math.abs(
    (edgeMidpoint.x - wall.start.x) * nX + (edgeMidpoint.z - wall.start.z) * nZ
  );

  if (distToWall > tolerance) return false;

  // Project edge endpoints onto wall direction
  const wallVector = {
    x: wall.end.x - wall.start.x,
    z: wall.end.z - wall.start.z,
  };
  const wallLength = Math.sqrt(
    wallVector.x * wallVector.x + wallVector.z * wallVector.z
  );
  const wallDir = {
    x: wallVector.x / wallLength,
    z: wallVector.z / wallLength,
  };

  // Project edge start and end onto wall
  const edgeStartProj =
    (edge.start.x - wall.start.x) * wallDir.x +
    (edge.start.z - wall.start.z) * wallDir.z;
  const edgeEndProj =
    (edge.end.x - wall.start.x) * wallDir.x +
    (edge.end.z - wall.start.z) * wallDir.z;

  const edgeMin = Math.min(edgeStartProj, edgeEndProj);
  const edgeMax = Math.max(edgeStartProj, edgeEndProj);

  // Check if projected edge overlaps with wall segment [0, wallLength]
  return edgeMax >= -tolerance && edgeMin <= wallLength + tolerance;
}

/**
 * Check if shower edge aligns with a wall
 * @param {Object} edge - Edge {start: {x,z}, end: {x,z}}
 * @param {Object} wall - Wall segment {start: {x,z}, end: {x,z}}
 * @param {number} tolerance - Distance tolerance in meters
 * @returns {boolean} True if edge aligns with wall
 */
function isEdgeAlignedWithWall(edge, wall, tolerance = 0.08) {
  // 1. Check if edge is parallel to wall
  const edgeVector = normalize2D({
    x: edge.end.x - edge.start.x,
    z: edge.end.z - edge.start.z,
  });

  const wallVector = normalize2D({
    x: wall.end.x - wall.start.x,
    z: wall.end.z - wall.start.z,
  });

  const dotProduct = Math.abs(
    edgeVector.x * wallVector.x + edgeVector.z * wallVector.z
  );
  const isParallel = dotProduct > 0.98; // ~11 degrees tolerance

  if (!isParallel) return false;

  // 2. Check if edge is close to wall (perpendicular distance to wall LINE,
  //    not segment — edge may extend beyond wall endpoints after resize)
  const wallDx = wall.end.x - wall.start.x;
  const wallDz = wall.end.z - wall.start.z;
  const wallLen = Math.sqrt(wallDx * wallDx + wallDz * wallDz);
  if (wallLen === 0) return false;
  const wallNormalX = -wallDz / wallLen;
  const wallNormalZ = wallDx / wallLen;
  const toEdgeX = edge.start.x - wall.start.x;
  const toEdgeZ = edge.start.z - wall.start.z;
  const distToWall = Math.abs(toEdgeX * wallNormalX + toEdgeZ * wallNormalZ);
  const isClose = distToWall < tolerance; // 5cm tolerance

  if (!isClose) return false;

  // 3. Check if edge overlaps with wall segment
  const overlaps = segmentsOverlap(edge, wall, tolerance);

  return overlaps;
}

/**
 * Transform polygon corners using a 4x4 transform matrix
 * @param {Array} corners - Array of corner points [{x, y, z}, ...]
 * @param {Array} transform - 16-element transform matrix
 * @returns {Array} Transformed corners
 */
function transformPolygonCorners(corners, transform) {
  if (!transform || transform.length !== 16) {
    return corners;
  }

  // Check if this is just a translation (identity rotation)
  const isIdentityRotation =
    Math.abs(transform[0] - 1) < 0.001 &&
    Math.abs(transform[5] - 1) < 0.001 &&
    Math.abs(transform[10] - 1) < 0.001 &&
    Math.abs(transform[1]) < 0.001 &&
    Math.abs(transform[2]) < 0.001 &&
    Math.abs(transform[4]) < 0.001 &&
    Math.abs(transform[6]) < 0.001 &&
    Math.abs(transform[8]) < 0.001 &&
    Math.abs(transform[9]) < 0.001;

  if (isIdentityRotation) {
    const offsetX = transform[12];
    const offsetY = transform[13];
    const offsetZ = transform[14];

    return corners.map((corner) => ({
      x: corner.x + offsetX,
      y: corner.y + offsetY,
      z: (corner.z || 0) + offsetZ,
    }));
  }

  return corners.map((corner) => {
    // Apply 4x4 transform matrix to the point
    const x = corner.x;
    const y = corner.y;
    const z = corner.z || 0;

    // Matrix multiplication
    const newX =
      transform[0] * x + transform[4] * y + transform[8] * z + transform[12];
    const newY =
      transform[1] * x + transform[5] * y + transform[9] * z + transform[13];
    const newZ =
      transform[2] * x + transform[6] * y + transform[10] * z + transform[14];

    return { x: newX, y: newY, z: newZ };
  });
}

/**
 * Get wall polygon corners as line segments
 * Prefers room.walls[] data to match bathroom analyzer coordinate space
 * @param {Object} room - Room object with walls or floors data
 * @returns {Array} Array of wall segments [{start, end, wallIndex, wallId}, ...]
 */
function getWallSegments(room) {
  // PREFERRED: Use room.walls[] data directly (same source as bathroom analyzer)
  // This ensures shower corners and room walls are in the same coordinate space
  if (room.walls && room.walls.length > 0) {
    const segments = [];

    room.walls.forEach((wall, index) => {
      let start, end;

      // Prefer alignment data (authoritative wall endpoints from wall-floor alignment)
      if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
        start = {
          x: wall.alignment.bottomLeft.x,
          z: wall.alignment.bottomLeft.z,
        };
        end = {
          x: wall.alignment.bottomRight.x,
          z: wall.alignment.bottomRight.z,
        };
      } else {
        // Fallback: compute from center/rotation/dimensions
        const rotationY = wall.rotation?.y || 0;
        const wallDir = {
          x: Math.cos(rotationY),
          z: Math.sin(rotationY),
        };
        const halfLength = wall.dimensions.x / 2;
        start = {
          x: wall.center.x - wallDir.x * halfLength,
          z: wall.center.z - wallDir.z * halfLength,
        };
        end = {
          x: wall.center.x + wallDir.x * halfLength,
          z: wall.center.z + wallDir.z * halfLength,
        };
      }

      segments.push({
        start,
        end,
        wallIndex: index,
        wallId: wall.id,
      });
    });

    return segments;
  }

  // FALLBACK: Use floor polygon corners (legacy behavior)
  if (!room.floors?.[0]?.polygonCorners) {
    return [];
  }

  const floor = room.floors[0];
  const corners = transformPolygonCorners(
    floor.polygonCorners,
    floor.transform
  );

  const segments = [];
  for (let i = 0; i < corners.length; i++) {
    const start = corners[i];
    const end = corners[(i + 1) % corners.length];
    segments.push({
      start: { x: start.x, z: start.z },
      end: { x: end.x, z: end.z },
      wallIndex: i,
    });
  }

  return segments;
}

/**
 * Calculate shower wall tiling areas
 * Works for any shower type: corner, alcove, single-wall, or freestanding
 *
 * @param {Array} showerFootprint - 4 corners of shower base [{x, z}, ...]
 * @param {Object} room - Room object with walls data
 * @param {number} tilingHeight - Height to tile in meters (default 2.4m for shower code compliance)
 * @returns {Object} Tiling information for each side
 */
function calculateShowerWallTiling(
  showerFootprint,
  room,
  tilingHeight = 2.4,
  wallTolerance = 0.08
) {
  if (!showerFootprint || showerFootprint.length < 3) {
    return {
      sides: [],
      totalArea: 0,
      tiledSideCount: 0,
      showerType: 'invalid',
    };
  }

  const tilingSides = [];

  // Build edges from consecutive polygon points (supports any N-gon)
  const edges = [];
  for (let i = 0; i < showerFootprint.length; i++) {
    edges.push({
      start: showerFootprint[i],
      end: showerFootprint[(i + 1) % showerFootprint.length],
      index: i,
    });
  }

  // Get wall segments from room
  const walls = getWallSegments(room);

  // For each edge, check if it aligns with a wall
  edges.forEach((edge) => {
    const edgeLength = distance2D(edge.start, edge.end);

    // Check against all room walls
    walls.forEach((wall) => {
      const aligned = isEdgeAlignedWithWall(edge, wall, wallTolerance);
      if (aligned) {
        // Calculate wall angle (normal direction facing into room)
        // Wall direction is from start to end
        const wallDx = wall.end.x - wall.start.x;
        const wallDz = wall.end.z - wall.start.z;
        // Normal is perpendicular to wall direction (rotate 90° CCW)
        const wallAngle = Math.atan2(wallDx, wallDz);

        tilingSides.push({
          edgeIndex: edge.index,
          wallIndex: wall.wallIndex,
          wallId: wall.wallId,
          length: edgeLength,
          height: tilingHeight,
          area: edgeLength * tilingHeight,
          startPoint: edge.start,
          endPoint: edge.end,
          wallAngle: wallAngle,
        });
      }
    });
  });

  // Determine shower type based on number of tiled sides
  let showerType;
  switch (tilingSides.length) {
    case 0:
      showerType = 'freestanding';
      break;
    case 1:
      showerType = 'single-wall';
      break;
    case 2:
      showerType = 'corner';
      break;
    case 3:
      showerType = 'alcove';
      break;
    case 4:
      showerType = 'enclosed';
      break;
    default:
      showerType = 'unknown';
  }

  const totalArea = tilingSides.reduce((sum, side) => sum + side.area, 0);

  return {
    sides: tilingSides,
    totalArea,
    tiledSideCount: tilingSides.length,
    showerType,
    tilingHeight,
  };
}

/**
 * Calculate shower tile region on a wall
 * @param {Object} shower - The shower fixture object with center, dimensions, rotation
 * @param {Object} wall - The wall object with center, dimensions, rotation
 * @param {Object} tilingSide - Optional tiling side data with startPoint/endPoint
 * @returns {Object|null} Region data {x, y, width, height} in percentages, or null if calculation fails
 */
/**
 * The wet-wall box for a tub/shower COMBO.
 *
 * A combo has no shower object — `findShowerTilingTarget` hands the tiler the
 * BATHTUB, which is right for the footprint (it is the same alcove) and wrong
 * for the height: `calculateShowerWallRegion` takes its vertical extent from
 * `dimensions.y`, and a tub's is ~20". So the tile came out 20" tall, topping
 * out at the rim — hidden behind the apron, with bare wall everywhere the
 * shower actually wets. Measured on Dako / Scan 2 "bathroom C": a 20.2" band
 * at y=0 on a 95.1" wall.
 *
 * The height a combo needs is the one the analyzer already assigns it —
 * `SHOWER_ENCLOSURE_HEIGHT_M`, on the stated grounds that a shower over a tub
 * needs the same enclosure as a shower.
 *
 * It runs from the FLOOR rather than the tub rim. The rim is where an alcove
 * surround visually begins, but the bottom 20" is behind the tub either way,
 * and anchoring at the floor keeps the tile correct when the tub is later
 * swapped or resized — a rim-anchored region leaves a bare strip the moment a
 * shorter bath goes in.
 *
 * Horizontally it widens to the ALCOVE for the same reason: the wet wall is
 * the recess, and the bath is what stands in it — across the back wall to the
 * recess, and back along the end walls to the back wall itself.
 *
 * @param {{center, dimensions, rotation}} tub
 * @param {Array} [walls] room walls — omit for the height-only answer
 * @returns {Object} a tub-shaped box spanning the wet wall
 */
function tubShowerWetZone(tub, walls = []) {
  const tubHeight = tub?.dimensions?.y;
  if (!tub?.center || !tubHeight) return tub;

  let center = { ...tub.center };
  let dimensions = { ...tub.dimensions };

  // --- Vertical: the enclosure, not the bath ---
  // A tub taller than a shower enclosure is not a thing; if the data says so,
  // leave the height alone rather than emitting an inverted region.
  if (SHOWER_ENCLOSURE_HEIGHT_M > tubHeight) {
    const floorY = tub.center.y - tubHeight / 2;
    center.y = floorY + SHOWER_ENCLOSURE_HEIGHT_M / 2;
    dimensions.y = SHOWER_ENCLOSURE_HEIGHT_M;
  }

  // --- Horizontal: the alcove, not the bath ---
  // The wet wall is the recess, and the bath is merely what stands in it. A
  // tub narrower than its alcove (22 of 24 in dev, by 0.2" to 34") otherwise
  // leaves a bare vertical strip in each corner.
  //
  // `alcoveRecess` casts from the tub's centre along ±width to the nearest
  // wall facing it and returns null unless BOTH directions find one — so an
  // open end never extends, and a tub on a long wall is not tiled end to end.
  // `centerShift` comes with the width because a tub is rarely centred in its
  // recess; taking the width without it hangs the region off one end.
  const recess = walls?.length ? alcoveRecess(tub, walls) : null;
  if (recess && recess.width > dimensions.x) {
    dimensions.x = recess.width;
    center.x += recess.centerShift.x;
    center.z += recess.centerShift.z;
  }

  // --- Depth: back to the wall ---
  // The alcove's END walls are tiled across the tub's DEPTH, so a tub that
  // does not touch its back wall leaves a bare strip in each corner — the
  // width fix above cannot reach it, because it is the other axis. Dako /
  // Scan 2 "bathroom C" sits 1.97" off, and 4 of dev's 24 combos do (see
  // #1890 — historical placement, not something this can repair; the slot
  // behind the bath stays until they are re-placed). Extending the wet zone
  // back to the wall plane is right whatever the cause: the wall between the
  // bath and the corner is wet wall. A flush tub extends by zero.
  const backWall = (walls || []).find((w) =>
    (tub.parentWallIds || []).includes(w.id)
  );
  if (backWall?.center && backWall.dimensions) {
    const ry = backWall.rotation?.y || 0;
    const n = { x: Math.sin(ry), z: Math.cos(ry) };
    const toCentre =
      (center.x - backWall.center.x) * n.x +
      (center.z - backWall.center.z) * n.z;
    // Signed so a tub on either face of the wall reads the same way.
    const gap = Math.abs(toCentre) - dimensions.z / 2;
    if (gap > 0) {
      const sign = Math.sign(toCentre) || 1;
      dimensions.z += gap;
      center.x -= n.x * sign * (gap / 2);
      center.z -= n.z * sign * (gap / 2);
    }
  }

  return { ...tub, center, dimensions };
}

/**
 * The tile's span along the wall, `[lo, hi]` in region-x percent: pushed out
 * by `marginIn` at each end, run on to an end of the wall it would stop within
 * `snapIn` of, then clamped to the wall. With no margin and no snap it is the
 * footprint itself.
 */
function tileSpanAlongWall(lo, hi, wallLength, marginIn, snapIn) {
  const toPct = (inches) =>
    wallLength > 0 ? ((inches * INCH_M) / wallLength) * 100 : 0;
  let start = lo - toPct(marginIn);
  let end = hi + toPct(marginIn);
  if (snapIn > 0) {
    // "Within 4"" includes 4" itself, whatever the float arithmetic says.
    const snap = toPct(snapIn) + 1e-9;
    if (start <= snap) start = 0;
    if (end >= 100 - snap) end = 100;
  }
  const clamp = (v) => Math.max(0, Math.min(100, v));
  return [clamp(start), clamp(end)];
}

/**
 * The tile's extent up the wall, `{ y, height }` in percent. `height` is the
 * rule's choice — Standard runs 84" up from the shower's floor, Full height
 * to the top of the wall — or null for the old answer, the shower model's own
 * height.
 */
function tileSpanUpWall(shower, wall, height) {
  // Percentages of the DRAWN face (`wallFace`), which the 3D tiles over.
  const face = wallFaceSpanY(wall);
  const wallHeight = face.height;
  const wallBottom = face.bottom;
  const showerHeight = shower.dimensions.y;
  const showerBottom = shower.center.y - showerHeight / 2;
  const bottomPct = ((showerBottom - wallBottom) / wallHeight) * 100;

  if (height === SHOWER_TILE_HEIGHTS.FULL) {
    const y = Math.max(0, bottomPct);
    return { y, height: Math.max(0, 100 - y) };
  }
  if (height === SHOWER_TILE_HEIGHTS.STANDARD) {
    const top =
      bottomPct +
      ((SHOWER_TILE_STANDARD_HEIGHT_IN * INCH_M) / wallHeight) * 100;
    const y = Math.max(0, bottomPct);
    return { y, height: Math.max(0, Math.min(100, top) - y) };
  }
  return {
    y: bottomPct,
    height: Math.min(100, (showerHeight / wallHeight) * 100),
  };
}

/**
 * @param {Object} [options] the shower tile rule — pass
 *   `showerTileRegionOptions(tileHeight, shower)`; omitted, the region is the
 *   footprint exactly at the shower model's height (the answer before the rule)
 * @param {number} [options.marginIn] tile past each end of the footprint
 * @param {number} [options.snapIn] run on to a wall end this close
 * @param {'standard'|'full'|null} [options.height]
 */
function calculateShowerWallRegion(
  shower,
  wall,
  tilingSide = null,
  options = {}
) {
  const { marginIn = 0, snapIn = 0, height = null } = options || {};
  if (
    !shower ||
    !wall ||
    !shower.center ||
    !wall.center ||
    !shower.dimensions ||
    !wall.dimensions
  ) {
    return null;
  }

  // Wall dimensions (its height is read off the drawn face, `tileSpanUpWall`)
  const wallWidth = wall.dimensions.x || wall.dimensions.width;

  // Use bottomCorners if available (definitive 3D coordinate system)
  // NOTE: bottomCorners[0] = UV 100% (right), bottomCorners[1] = UV 0% (left)
  // This is opposite from PlaneGeometry UVs, so we flip the result after projection
  let endpoint1, endpoint2;
  if (wall.bottomCorners && wall.bottomCorners.length >= 2) {
    endpoint1 = {
      x: wall.bottomCorners[0].x,
      z: wall.bottomCorners[0].z,
    };
    endpoint2 = {
      x: wall.bottomCorners[1].x,
      z: wall.bottomCorners[1].z,
    };
  } else {
    // Fallback: Calculate from center and rotation
    const wallRotationY = wall.rotation?.y || 0;
    const wallTangent = {
      x: Math.cos(wallRotationY),
      z: -Math.sin(wallRotationY),
    };
    const halfWidth = wallWidth / 2;
    endpoint1 = {
      x: wall.center.x + wallTangent.x * halfWidth,
      z: wall.center.z + wallTangent.z * halfWidth,
    };
    endpoint2 = {
      x: wall.center.x - wallTangent.x * halfWidth,
      z: wall.center.z - wallTangent.z * halfWidth,
    };
  }

  // Project shower edge onto wall to find exact position: the footprint's
  // span `[spanLo, spanHi]` in region-x percent.
  let spanLo, spanHi;

  if (tilingSide && tilingSide.startPoint && tilingSide.endPoint) {
    // Project both edge endpoints onto wall line segment
    // Wall direction vector (from endpoint1 to endpoint2)
    const wallVector = {
      x: endpoint2.x - endpoint1.x,
      z: endpoint2.z - endpoint1.z,
    };
    const wallLengthSq =
      wallVector.x * wallVector.x + wallVector.z * wallVector.z;

    // Project shower edge start point onto wall
    const vStart = {
      x: tilingSide.startPoint.x - endpoint1.x,
      z: tilingSide.startPoint.z - endpoint1.z,
    };
    const tStart =
      (vStart.x * wallVector.x + vStart.z * wallVector.z) / wallLengthSq;

    // Project shower edge end point onto wall
    const vEnd = {
      x: tilingSide.endPoint.x - endpoint1.x,
      z: tilingSide.endPoint.z - endpoint1.z,
    };
    const tEnd = (vEnd.x * wallVector.x + vEnd.z * wallVector.z) / wallLengthSq;

    // Convert to percentages (t is in [0, 1] range where 0 = endpoint1, 1 = endpoint2)
    const percentStart = tStart * 100;
    const percentEnd = tEnd * 100;

    // Region spans from min to max of the projected points
    // Clamp to [0, 100] to handle cases where shower edge extends beyond wall
    // (e.g., when partition wall has a gap from the room boundary)
    const minPercent = Math.max(0, Math.min(percentStart, percentEnd));
    const maxPercent = Math.min(100, Math.max(percentStart, percentEnd));

    // IMPORTANT: bottomCorners[0] is actually at UV 100%, bottomCorners[1] is at UV 0%
    // This is opposite from PlaneGeometry UVs, so we need to flip
    spanLo = 100 - maxPercent;
    spanHi = 100 - minPercent;
  } else {
    // Fallback to shower center (legacy behavior)
    const wallVector = {
      x: endpoint2.x - endpoint1.x,
      z: endpoint2.z - endpoint1.z,
    };
    const wallLengthSq =
      wallVector.x * wallVector.x + wallVector.z * wallVector.z;

    // Project shower center onto wall
    const showerCenter = { x: shower.center.x, z: shower.center.z };
    const vToShower = {
      x: showerCenter.x - endpoint1.x,
      z: showerCenter.z - endpoint1.z,
    };
    const tShower =
      (vToShower.x * wallVector.x + vToShower.z * wallVector.z) / wallLengthSq;

    // Use shower dimensions for width
    const showerWidth = shower.dimensions.x;
    const showerDepth = shower.dimensions.z;
    const edgeLength = Math.max(showerWidth, showerDepth);

    const widthPercent = (edgeLength / wallWidth) * 100;
    const centerPercent = tShower * 100;
    spanLo = centerPercent - widthPercent / 2;
    spanHi = spanLo + widthPercent;
  }

  const [lo, hi] = tileSpanAlongWall(
    spanLo,
    spanHi,
    distance2D(endpoint1, endpoint2),
    marginIn,
    snapIn
  );
  const vertical = tileSpanUpWall(shower, wall, height);

  return {
    x: lo,
    y: vertical.y,
    width: hi - lo,
    height: vertical.height,
  };
}

/**
 * Build shower footprint from center and dimensions with rotation
 * @param {Object} shower - Shower object with center, dimensions, rotation
 * @returns {Array} Array of 4 corner points [{x, z}, ...]
 */
function buildShowerFootprint(shower) {
  const centerX = shower.center.x;
  const centerZ = shower.center.z;
  const halfWidth = (shower.dimensions?.x || 0.9) / 2;
  const halfDepth = (shower.dimensions?.z || 0.9) / 2;

  // Apply rotation if present
  const rotationY = shower.rotation?.y || 0;
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);

  // Generate 4 corners (counter-clockwise when viewed from above)
  const localCorners = [
    { x: -halfWidth, z: -halfDepth },
    { x: halfWidth, z: -halfDepth },
    { x: halfWidth, z: halfDepth },
    { x: -halfWidth, z: halfDepth },
  ];

  return localCorners.map((corner) => ({
    x: centerX + corner.x * cos - corner.z * sin,
    z: centerZ + corner.x * sin + corner.z * cos,
  }));
}

/**
 * Build a fixture footprint using tub-surround-polygon when available,
 * falling back to the rectangular buildShowerFootprint.
 * @param {Object} obj - Object with center, dimensions, rotation, and
 *                        optionally tub-surround-polygon
 * @returns {Array} Array of corner points [{x, z}, ...]
 */
function buildFixtureFootprint(obj) {
  const poly = obj['tub-surround-polygon'];
  if (poly?.points?.length >= 5) {
    const I2M = 0.0254;
    const rotAngle = -(obj.rotation?.y || 0);
    const cos = Math.cos(rotAngle);
    const sin = Math.sin(rotAngle);
    return poly.points.map((p) => ({
      x: obj.center.x + p.x * I2M * cos - p.z * I2M * sin,
      z: obj.center.z + p.x * I2M * sin + p.z * I2M * cos,
    }));
  }
  return buildShowerFootprint(obj);
}

// Export all functions
module.exports = {
  distance2D,
  normalize2D,
  pointToLineSegmentDistance,
  segmentsOverlap,
  isEdgeAlignedWithWall,
  transformPolygonCorners,
  getWallSegments,
  calculateShowerWallTiling,
  calculateShowerWallRegion,
  tubShowerWetZone,
  buildShowerFootprint,
  buildFixtureFootprint,
  SHOWER_TILE_MARGIN_IN,
  SHOWER_TILE_SNAP_IN,
  SHOWER_TILE_STANDARD_HEIGHT_IN,
  SHOWER_TILE_HEIGHTS,
  DEFAULT_SHOWER_TILE_HEIGHT,
  isShowerTileHeight,
  showerTileRegionOptions,
};
